import type { LatLng } from '~/_shared/types/latLng';
import type { MultiPolygon } from '~/_shared/types/polygon/polygon.types';
import {
  isDriveTimePolygon, type Proximity,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { notUndefined } from '~/_shared/utils/typeGuards';
import { ExportReportTemplateSelectorArea } from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import {
  AGS_AREA_VALIDATION_ERROR_CODES, validatePolygonForAgsApi, validateRadiusForAgsApi,
} from '~/exportReport/validation/exportReportApiValidation.helpers';
import type { ExportReportAreaQuery } from '~/exportReport/validation/exportReportAreas.worker.helpers';
import { getExportReportAreas } from '~/exportReport/validation/getExportReportAreas';
import {
  type DemographicRegion, DemographicRegions,
} from '~/store/demographics/demographics.types';

// Mexico has no AGS demographic region yet.
const AREA_TO_DEMOGRAPHIC_REGION: Partial<Record<ExportReportTemplateSelectorArea, DemographicRegion>> = {
  [ExportReportTemplateSelectorArea.USA]: DemographicRegions.US,
  [ExportReportTemplateSelectorArea.Canada]: DemographicRegions.CA,
};

export type DataLayerAreaValidationResult = Readonly<{
  isValid: false;
  errorCode: string;
} | {
  isValid: true;
  // null when the area lookup failed - metrics should not be filtered by region then
  supportedRegions: ReadonlyArray<DemographicRegion> | null;
}>;

const getSupportedRegions = async (query: ExportReportAreaQuery): Promise<ReadonlyArray<DemographicRegion> | null> => {
  try {
    const areas = await getExportReportAreas(query);
    return areas.map(area => AREA_TO_DEMOGRAPHIC_REGION[area]).filter(notUndefined);
  }
  catch (e) {
    console.error(e);
    return null;
  }
};

export const validateDataLayerPolygonArea = async (multiPolygon: MultiPolygon): Promise<DataLayerAreaValidationResult> => {
  const polygonValidationResult = validatePolygonForAgsApi(multiPolygon);

  if (!polygonValidationResult.isValid) {
    return { isValid: false, errorCode: polygonValidationResult.errorCode };
  }

  return { isValid: true, supportedRegions: await getSupportedRegions({ type: 'polygon', paths: multiPolygon }) };
};

export const validateDataLayerProximityArea = async (
  proximity: Proximity,
  center: LatLng | undefined,
  spreadsheetRowId?: SpreadsheetRowId,
): Promise<DataLayerAreaValidationResult> => {
  if (isDriveTimePolygon(proximity)) {
    return proximity.data.paths
      ? validateDataLayerPolygonArea(proximity.data.paths)
      : { isValid: true, supportedRegions: [] };
  }

  if (!validateRadiusForAgsApi(proximity, spreadsheetRowId)) {
    return { isValid: false, errorCode: AGS_AREA_VALIDATION_ERROR_CODES.RadiusTooLarge };
  }

  return {
    isValid: true,
    supportedRegions: center ? await getSupportedRegions({ type: 'point', point: center }) : [],
  };
};
